/**
 * Les décisions du tiroir de club qui ne sont pas de la mise en page.
 *
 * Extraites pour la même raison que `viewState.ts` : dans le composant, chacune
 * n'est éprouvable qu'en montant tout le tiroir — et ce sont précisément les
 * endroits où une erreur ne se verrait pas à l'écran. Quelles entrées sont
 * arbitrables, et lesquelles sont seulement ANNULABLES, détermine ce qu'un
 * président de ligue peut enregistrer ou défaire pendant que la fenêtre FAF est
 * ouverte ; le nettoyage d'un numéro de licence décide si une
 * ligne porte un identifiant ou une tabulation invisible ; la répartition de
 * l'effectif décide de quel côté tombe un joueur parti ailleurs ; les deux
 * lectures d'erreur d'écriture décident de ce que l'opérateur lit quand un
 * arbitrage échoue ; le verdict de fin de lot décide s'il lit quelque chose du
 * tout ; et le bandeau de tête décide de ce qu'on lui donne pour explication
 * quand aucun bouton n'est offert.
 *
 * Aucun état n'est écrit ici, et il ne doit pas y en avoir : la fenêtre
 * d'engagement reste ouverte, donc tout verdict sur un joueur se recalcule à
 * chaque rendu depuis les RPC.
 */
import type { RosterEntryRow } from "@/hooks/useRosterReconciliation";
import type { EngagementPlayerState } from "./types";

export type EngagementBucketKey =
  | "applied_add"
  | "applied_move"
  | "ambiguous"
  | "add"
  | "move"
  | "remove"
  | "applied_remove";

/** La forme minimale d'une entrée pour la répartir : son statut, et — s'il est
 *  `accepted` — le verdict qui était en attente au moment de l'acceptation. */
export interface RosterEntryBucketInput {
  status: RosterEntryRow["status"];
  acceptedFromStatus?: RosterEntryRow["acceptedFromStatus"];
}

/** La pile d'une entrée, ou `null` si elle n'en a aucune.
 *
 *  Le tiroir n'est plus une file d'attente, c'est une REVUE : l'import applique
 *  les arrivées et les mutations (`apply_roster_entries_auto`), et la ligue relit
 *  ce qui a été écrit avec un « Annuler » par ligne. Le « pourquoi » est dans
 *  `supabase/migrations/20260923085929_roster_auto_apply_and_revert.sql` — en un
 *  mot : `pending_move` et `auto_applied` sortent de la même branche du moteur
 *  d'appariement et ne diffèrent que par le club où NOTRE base croit que la
 *  personne joue, et la mutation elle-même a déjà été validée par la ligue dans le
 *  portail FAF. Sur 2026-2027, 419 des 420 « décisions » ne portaient aucune
 *  information.
 *
 *  Les piles `applied_*` ne se lisent donc PAS sur `status` seul : `accepted` est
 *  le même statut pour les trois, et c'est `accepted_from_status` qui dit ce qui a
 *  été écrit, donc ce qu'« Annuler » défera. Une ligne acceptée AVANT cette
 *  colonne la porte à `NULL` : elle n'a aucun contexte d'annulation en base
 *  (`revert_roster_entry` lève `has no revert context`), donc elle ne tombe dans
 *  aucune pile plutôt que d'offrir un bouton qui échouerait.
 *
 *  `add`, `move` et `remove` restent des piles arbitrables à la main : la première
 *  n'est plus alimentée que par ce que l'application automatique n'a pas pu
 *  écrire — un club hors groupe régulier, où la RPC ne peut pas dériver la ligue,
 *  ou un échec par entrée — et `remove` n'existe que sous
 *  `import-licence-portal.js --with-removals`, jamais passé tant que la fenêtre
 *  d'engagement est ouverte.
 *
 *  `auto_applied` et `rejected` n'ont pas de pile : le premier n'a rien changé
 *  d'autre qu'une licence déjà à jour, le second est un refus que l'import
 *  reporte. */
export function rosterEntryBucket(entry: RosterEntryBucketInput): EngagementBucketKey | null {
  if (entry.status === "accepted") {
    switch (entry.acceptedFromStatus) {
      case "pending_add":
        return "applied_add";
      case "pending_move":
        return "applied_move";
      case "pending_remove_ref":
        return "applied_remove";
      default:
        return null;
    }
  }
  switch (entry.status) {
    case "pending_add":
      return "add";
    case "pending_move":
      return "move";
    case "ambiguous":
      return "ambiguous";
    case "pending_remove_ref":
      return "remove";
    default:
      return null;
  }
}

/** Ordre d'affichage : ce qui a été ENREGISTRÉ cette nuit d'abord, puisque c'est
 *  ce que la ligue vient relire ; l'ambiguïté ensuite, seule décision réelle qui
 *  reste ; les piles à arbitrer à la main après, parce qu'elles sont désormais
 *  l'exception ; les retraits en dernier, seuls à fermer quelque chose
 *  d'existant. */
export const ENGAGEMENT_BUCKET_ORDER: readonly EngagementBucketKey[] = [
  "applied_add",
  "applied_move",
  "ambiguous",
  "add",
  "move",
  "remove",
  "applied_remove",
];

/** Les piles rendues même vides. « Rien à traiter ici » sur « À identifier » est
 *  une information : l'opérateur vient vérifier qu'il ne reste rien à trancher.
 *
 *  Les autres n'apparaissent QUE si elles portent des lignes, et c'est ce qui
 *  tient la lecture de l'écran. « Arrivées en attente · 0 » serait le cas normal
 *  de tous les clubs placés — un en-tête permanent pour une pile que l'import
 *  vide chaque nuit. « Retraits FAF · 0 » posé à côté de « Non relicenciés · 26 »
 *  ferait lire un club sans retrait à un club dont personne n'est encore
 *  licencié. Et « Retraits enregistrés · 0 » parlerait d'une action que personne
 *  ne peut avoir faite avant la clôture. */
const ALWAYS_VISIBLE: readonly EngagementBucketKey[] = ["applied_add", "applied_move", "ambiguous"];

/** Quelles piles le tiroir rend.
 *
 *  Pris sur les lignes réellement chargées, et non sur un compteur de la vue
 *  d'ensemble : les deux comptent les mêmes entrées, mais la pile doit apparaître
 *  exactement quand elle a quelque chose à montrer. */
export function bucketVisibility(
  buckets: Record<EngagementBucketKey, readonly unknown[]>,
): EngagementBucketKey[] {
  return ENGAGEMENT_BUCKET_ORDER.filter(
    (key) => ALWAYS_VISIBLE.includes(key) || buckets[key].length > 0,
  );
}

/** Ce qu'une ligne de la pile propose : défaire une écriture, choisir entre
 *  plusieurs personnes, ou trancher un verdict en attente.
 *
 *  Une seule décision, prise ici plutôt que dans le rendu, parce que « quelle pile
 *  offre quel bouton » est exactement ce qui ne se voit pas à l'écran quand c'est
 *  faux : un « Accepter » sur une ligne déjà appliquée lèverait
 *  `already resolved`, et un « Annuler » sur une ligne en attente lèverait
 *  `is not applied`. */
export type EngagementBucketAction = "revert" | "pick" | "arbitrate";

export function bucketAction(bucket: EngagementBucketKey): EngagementBucketAction {
  if (bucket === "applied_add" || bucket === "applied_move" || bucket === "applied_remove") {
    return "revert";
  }
  return bucket === "ambiguous" ? "pick" : "arbitrate";
}

/** Comment « tout accepter » se comporte sur chaque pile.
 *
 *  `none` sur tout ce qui n'est pas arbitrable : sur l'ambiguïté accepter en masse
 *  reviendrait à choisir une correspondance au hasard entre plusieurs personnes,
 *  et sur les piles appliquées il n'y a rien à accepter. Il n'y a délibérément
 *  AUCUN « tout annuler » : l'annulation est le rattrapage d'un cas particulier,
 *  et un bouton qui défait quarante écritures d'un clic rendrait à la ligue
 *  exactement le pouvoir de masse que cet écran vient de lui retirer.
 *
 *  `confirm` sur les retraits, et c'est la seule différence de fond entre les
 *  piles arbitrables. Accepter une arrivée ou une mutation crée ou repointe une
 *  adhésion ; accepter un retrait DÉSACTIVE l'adhésion de la saison. À ~36 lignes
 *  par club, un bouton direct fermerait trente-six adhésions sur un clic, y
 *  compris si l'import avec retraits a tourné avant que tous les clubs aient
 *  déposé. Le refuser tout court n'était pas une option : 3 480 lignes à accepter
 *  une par une, c'est une pile que personne ne vide, donc un écran qui montre sans
 *  permettre d'agir. */
export function bucketBulkAcceptMode(bucket: EngagementBucketKey): "direct" | "confirm" | "none" {
  if (bucketAction(bucket) !== "arbitrate") return "none";
  return bucket === "remove" ? "confirm" : "direct";
}

/** Générique sur la forme minimale plutôt que sur `RosterEntryRow` : la fonction
 *  ne lit que `status` et `acceptedFromStatus`, et une fixture de test n'a pas à
 *  inventer les douze autres champs pour éprouver la répartition. */
export function groupEntriesByBucket<T extends RosterEntryBucketInput>(
  entries: readonly T[],
): Record<EngagementBucketKey, T[]> {
  const grouped = {
    applied_add: [],
    applied_move: [],
    ambiguous: [],
    add: [],
    move: [],
    remove: [],
    applied_remove: [],
  } as Record<EngagementBucketKey, T[]>;
  for (const entry of entries) {
    const key = rosterEntryBucket(entry);
    if (key) grouped[key].push(entry);
  }
  return grouped;
}

/** Un numéro de licence tel qu'il sort de la base peut porter une tabulation de
 *  tête — `"\t25N03J2615"` est en production. Le nettoyage est ici, dans la
 *  présentation, et non dans les données : ce numéro est la clé de rapprochement
 *  avec les documents papier FAF/LIRF, donc la valeur stockée doit rester celle
 *  que le portail a servie, au caractère près.
 *
 *  Une valeur entièrement blanche rend `null` et non `""` : sans cela l'appelant
 *  afficherait une chaîne vide là où il croit afficher un numéro, ce qui se lit
 *  comme un libellé manquant plutôt que comme une licence absente. `null` le
 *  renvoie vers son tiret ou son « aucune licence ». */
export function cleanLicence(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Les deux lectures en LECTURE SEULE du tiroir, prises dans l'effectif reporté.
 *
 *  `qualified` n'apparaît dans aucune des deux : un joueur dont la licence est
 *  déposée par son club n'a rien à traiter. Le faire tomber dans `notLicensed`
 *  relancerait un club pour un joueur déjà acquis ; le faire tomber dans
 *  `departed` lui dirait qu'il l'a perdu. */
export function partitionClubPlayers<T extends { state: EngagementPlayerState }>(
  players: readonly T[],
): { relicensed: T[]; notLicensed: T[]; departed: T[] } {
  return {
    relicensed: players.filter((p) => p.state === "qualified"),
    notLicensed: players.filter((p) => p.state === "pending"),
    departed: players.filter((p) => p.state === "departed"),
  };
}

/** Forme minimale d'une erreur d'écriture : seul le `message` est lu. Typé ainsi
 *  plutôt que sur `PostgrestError` parce que `mutateAsync` rejette un `any`, et
 *  qu'un test n'a pas à fabriquer un `code`/`details`/`hint` dont la décision ne
 *  dépend pas. */
export interface EngagementWriteErrorLike {
  message?: string | null;
}

/** Les deux seules clés que le tiroir affiche sur un échec d'arbitrage. On rend
 *  la CLÉ i18n et non la chaîne traduite : le module reste pur, donc éprouvable
 *  sans i18next, et le rendu garde le choix de la langue au moment du toast. */
export type EngagementWriteErrorKey = "common:errors.unauthorized" | "common:errors.save_failed";

/** Littéral de `resolve_roster_entry` lorsqu'une entrée est déjà tranchée :
 *  `RAISE EXCEPTION 'entry % already resolved (%)'`, soit
 *  `entry <uuid> already resolved (accepted)` à l'arrivée.
 *
 *  Ce qui est comparé est exactement la constante ci-dessous : la plus longue
 *  suite réellement fixe du littéral, celle qui sépare les deux `%` — espace de
 *  tête et parenthèse ouvrante comprises. Ce qui n'est PAS comparé : le préfixe
 *  `entry `, l'UUID, le statut, et la parenthèse fermante. La parenthèse est
 *  gardée parce qu'elle est ce qui distingue le `RAISE` de la RPC d'une phrase
 *  quelconque contenant « already resolved » : ce prédicat est la seule chose
 *  entre un échec réel et un saut silencieux, donc il ne doit pas être plus large
 *  que nécessaire.
 *
 *  Pourquoi le texte et non le `code` : les deux refus de la RPC qui nous
 *  intéressent — celui-ci et `Not allowed` — sont des `RAISE EXCEPTION` sans
 *  `ERRCODE`, donc tous deux `P0001`. Le code ne les distingue pas ; le message
 *  est la seule discrimination disponible. Ce qui casse cette lecture est donc
 *  connu : modifier le texte du `RAISE` dans la RPC sans toucher ces constantes.
 *  Voir `supabase/migrations/20260720000015_transfer_and_roster_resolve_via_ensure.sql`. */
const RPC_ALREADY_RESOLVED = " already resolved (";

/** Littéral de `revert_roster_entry` sur une ligne qui n'est pas appliquée :
 *  `RAISE EXCEPTION 'entry % is not applied (%)'`. Même découpe que ci-dessus — la
 *  plus longue suite fixe entre les deux `%`, parenthèse ouvrante comprise. Voir
 *  `supabase/migrations/20260923085929_roster_auto_apply_and_revert.sql`. */
const RPC_NOT_APPLIED = " is not applied (";

/** Littéral du refus d'autorisation de la RPC, levé quand la ligue n'est pas
 *  dérivable ou que `can_manage_league(v_league)` est faux. Même dépendance au
 *  texte que ci-dessus, pour la même raison. */
const RPC_NOT_ALLOWED = "Not allowed";

/** « Cette écriture a échoué parce que l'entrée était déjà tranchée ».
 *
 *  Sert à traiter ce cas comme un non-événement, et non comme un échec : la
 *  boucle « Tout accepter » itère une photographie prise avant le premier appel,
 *  donc un second clic — ou un import concurrent — la fait repasser sur des
 *  entrées désormais `accepted`. La RPC lève alors, alors que l'état voulu par
 *  l'opérateur est précisément atteint. Le compter en échec ferait afficher une
 *  erreur nommée pour chaque ligne qui a, en réalité, réussi. */
export function isAlreadyResolvedWriteError(
  error: EngagementWriteErrorLike | null | undefined,
): boolean {
  return typeof error?.message === "string" && error.message.includes(RPC_ALREADY_RESOLVED);
}

/** « Cette annulation a échoué parce que la ligne n'était plus appliquée ».
 *
 *  Même traitement que ci-dessus, et pour la même raison : c'est le second clic.
 *  La confirmation reste ouverte pendant l'appel, mais un rechargement, un onglet
 *  voisin ou un import concurrent peuvent avoir défait la ligne entre-temps.
 *  L'état voulu est atteint ; afficher « l'enregistrement n'a pas eu lieu » là où
 *  la ligne EST annulée dirait le contraire de ce que montre l'écran. */
export function isAlreadyRevertedWriteError(
  error: EngagementWriteErrorLike | null | undefined,
): boolean {
  return typeof error?.message === "string" && error.message.includes(RPC_NOT_APPLIED);
}

/** Clé de message pour un échec d'arbitrage réellement à signaler.
 *
 *  Aucun `message` de Postgres n'est rendu : `e.message` est défini pour toute
 *  erreur PostgREST, donc sans ce mappage l'opérateur lisait `Not allowed`,
 *  `permission denied for table faf_roster_entries` ou l'UUID d'une entrée dans
 *  un toast français ou arabe — du latin brut dans une interface RTL, qui casse
 *  en plus l'ordre bidi. Le message brut est journalisé côté appelant.
 *
 *  Tout ce qui n'est pas le refus d'autorisation de la RPC tombe sur
 *  `save_failed` : l'opérateur ne peut agir que sur ces deux distinctions —
 *  « ce n'est pas à moi de trancher » et « l'enregistrement n'a pas eu lieu ». */
export function engagementWriteErrorKey(
  error: EngagementWriteErrorLike | null | undefined,
): EngagementWriteErrorKey {
  if (typeof error?.message === "string" && error.message.includes(RPC_NOT_ALLOWED)) {
    return "common:errors.unauthorized";
  }
  return "common:errors.save_failed";
}

/** Le décompte d'une passe de « Tout accepter », tel que la boucle le tient.
 *
 *  Les trois catégories sont exclusives et couvrent chaque ligne visitée : une
 *  entrée est appliquée, sautée parce qu'elle était déjà tranchée, ou réellement
 *  en échec. */
export interface EngagementAcceptAllTally {
  applied: number;
  skipped: number;
  failed: number;
}

/** Ce que le tiroir annonce à la fin de « Tout accepter ». */
export type EngagementAcceptAllOutcome = "applied" | "nothing" | "silent";

/** Le verdict de fin de lot a TROIS issues, et c'est tout l'objet de ce module :
 *  la version à deux issues (`if (ok > 0)`) laissait le lot entièrement redondant
 *  sans aucun toast — l'opérateur cliquait et rien ne se passait nulle part.
 *
 *  - `applied` : au moins une entrée est passée. Le nombre appliqué est annoncé.
 *  - `nothing` : rien d'appliqué et aucun échec réel, donc tout a été sauté comme
 *    déjà tranché. C'est le second clic sur un lot déjà réglé, ou un import
 *    concurrent : l'état voulu EST atteint, et le dire est un fait utile.
 *  - `silent` : rien d'appliqué mais au moins un échec réel. Chaque échec a déjà
 *    levé son propre toast nommé ; un « aucune entrée appliquée » par-dessus se
 *    lirait comme un second verdict, séparé et contradictoire.
 *
 *  `skipped` est lu et non seulement compté, et c'est ce qui fait tomber le lot
 *  vide (0/0/0) dans `silent` : l'appelant s'arrête aujourd'hui avant la boucle
 *  sur une pile vide, mais annoncer « aucune entrée appliquée » là où rien n'a
 *  même été tenté affirmerait un travail qui n'a pas eu lieu. La fonction est donc
 *  totale, sans dépendre de ce garde-là chez l'appelant. */
export function engagementAcceptAllOutcome(
  tally: EngagementAcceptAllTally,
): EngagementAcceptAllOutcome {
  if (tally.applied > 0) return "applied";
  if (tally.failed > 0) return "silent";
  return tally.skipped > 0 ? "nothing" : "silent";
}

/** Le bandeau de tête du tiroir : la CLÉ i18n de l'explication due quand aucun
 *  bouton d'arbitrage n'est rendu, ou `null` quand ils le sont.
 *
 *  Comme `engagementWriteErrorKey`, on rend une clé et non une phrase : le
 *  module reste pur, donc la précédence des deux explications s'éprouve sans
 *  i18next ni rendu. */
export type EngagementArbitrationNotice =
  | "engagements.arbitration_waiting_group"
  | "engagements.arbitration_reserved"
  | null;

/** Ce que le tiroir explique quand il n'offre pas d'arbitrage.
 *
 *  Deux causes, et elles ne se disent pas de la même façon :
 *
 *  - `inGroup` faux : le club n'est placé dans aucun groupe régulier de la
 *    saison, donc `resolve_roster_entry` ne peut pas dériver sa ligue et AUCUN
 *    rôle ne peut trancher. L'obstacle est le placement, pas le demandeur.
 *  - sinon `mayArbitrate` faux : le club est plaçable, c'est le lecteur qui n'a
 *    pas le rôle. Sans occurrence dans les données du jour, mais pas
 *    inatteignable pour autant : la vue d'ensemble se garde sur le SQL
 *    `can_manage_league`, `mayArbitrate` sur son MIROIR client, et le miroir
 *    rend `false` là où la base dit `true` pour un admin hybride
 *    (`lib/auth/canManageLeague.ts:12-15` — `hooks/useCurrentUser.ts:66-72` ne
 *    retient les rôles globaux que si aucune ligne `user_league_roles` n'existe).
 *    Ajouter une seule assignation scopée à un `admin` global — la façon
 *    ordinaire d'accorder une portée — fait donc tomber cette branche sur des
 *    clubs placés dont la RPC accepterait les écritures. C'est le message
 *    honnête de ce cas, et il coûte une comparaison.
 *
 *  L'ordre est le défaut corrigé, et non un détail de style : le bandeau ne
 *  testait que `mayArbitrate`, devenu faux sur les 24 clubs hors groupe de
 *  2026-2027 depuis que le garde s'appuie sur `in_group`. Le président de ligue
 *  qui en ouvrait un se voyait refuser un rôle qu'il détient. Inverser ces deux
 *  tests le réintroduit à l'identique, sans que rien ne casse ailleurs.
 *
 *  La combinaison contradictoire (`mayArbitrate` vrai, `inGroup` faux) n'est pas
 *  traitée à part parce qu'elle n'existe pas : `mayArbitrate` est
 *  `row.in_group && canManageLeague(…)`, donc il implique `inGroup`. */
export function engagementArbitrationNotice(input: {
  inGroup: boolean;
  mayArbitrate: boolean;
}): EngagementArbitrationNotice {
  if (!input.inGroup) return "engagements.arbitration_waiting_group";
  if (!input.mayArbitrate) return "engagements.arbitration_reserved";
  return null;
}
